import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-hot-toast";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  Minus,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  Info,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

interface MonthData {
  monthKey: string;
  monthLabel: string;
  isPartial: boolean;
  isProjected: boolean;
  callCount: number;
  actualMinutes: number;
  projectedMinutes: number | null;
  minimumMinutes: number;
  minimumRevenue: number;
  overageMinutes: number;
  overageRevenue: number;
  totalRevenue: number;
  actualTotalRevenue: number | null;
  underutilization: number;
  isAboveMinimum: boolean;
}

interface CustomerResult {
  id: string;
  name: string;
  serviceType: string;
  billingStatus: string;
  minimumMinutes: number;
  ratePerMinute: number;
  overageRatePerMinute: number;
  hasMinimum: boolean;
  latestPeriodOverageRevenue: number;
  data: MonthData[];
}

interface SummaryMonth {
  monthKey: string;
  monthLabel: string;
  isPartial: boolean;
  isProjected: boolean;
  totalCalls: number;
  totalActualMinutes: number;
  totalProjectedMinutes: number | null;
  totalMinimumRevenue: number;
  totalOverageRevenue: number;
  totalUsageRevenue: number;
  totalRevenue: number;
  totalUnderutilizationMinutes: number;
  accountsAboveMinimum: number;
  accountsAtOrBelowMinimum: number;
  accountsWithNoMinimum: number;
}

interface ApiData {
  months: string[];
  currentMonthKey: string | null;
  monthView: "completed" | "partial" | "projected";
  timezone: string;
  generatedAt: string;
  calculationBasis: string;
  summary: SummaryMonth[];
  customers: CustomerResult[];
}

// ── Formatting helpers ────────────────────────────────────────────────────────

const fmtCurrency = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtCurrencyFull = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtMinutes = (n: number) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ── Custom tooltip for stacked bar ───────────────────────────────────────────

const RevenueTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const isPartial = payload[0]?.payload?.isPartial;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-800 mb-1">{label}{isPartial ? " (in progress)" : ""}</p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: p.fill }} />
          <span className="text-gray-600">{p.name}:</span>
          <span className="font-medium">{fmtCurrencyFull(p.value)}</span>
        </div>
      ))}
      <div className="border-t border-gray-100 mt-1.5 pt-1.5 flex justify-between font-semibold text-gray-800">
        <span>Total</span>
        <span>{fmtCurrencyFull(payload.reduce((s: number, p: any) => s + p.value, 0))}</span>
      </div>
    </div>
  );
};

const VolumeTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const isPartial = payload[0]?.payload?.isPartial;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-800 mb-1">{label}{isPartial ? " (in progress)" : ""}</p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: p.stroke }} />
          <span className="text-gray-600">{p.name}:</span>
          <span className="font-medium">{p.value?.toLocaleString()}{p.name === "Minutes" ? " min" : ""}</span>
        </div>
      ))}
    </div>
  );
};

// ── Sparkline for per-customer rows ──────────────────────────────────────────

function CustomerSparkline({ data, hasMinimum }: { data: MonthData[]; hasMinimum: boolean }) {
  const minThreshold = data[0]?.minimumMinutes ?? 0;
  return (
    <ResponsiveContainer width="100%" height={56}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
        <XAxis dataKey="monthLabel" hide />
        <YAxis hide />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0]?.payload as MonthData;
            return (
              <div className="bg-white border border-gray-200 rounded shadow p-2 text-xs">
                <p className="font-semibold">{d.monthLabel}{d.isPartial ? "*" : ""}</p>
                <p>Actual: {fmtMinutes(d.actualMinutes)} min</p>
                {hasMinimum && <p>Min: {fmtMinutes(d.minimumMinutes)} min</p>}
              </div>
            );
          }}
        />
        {hasMinimum && (
          <ReferenceLine y={minThreshold} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={1} />
        )}
        <Line
          type="monotone"
          dataKey="actualMinutes"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={false}
          name="Minutes"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Mini revenue bar for per-customer rows ────────────────────────────────────

function CustomerMiniBar({ data }: { data: MonthData[] }) {
  const last4 = data.slice(-4);
  return (
    <ResponsiveContainer width="100%" height={56}>
      <BarChart data={last4} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
        <XAxis dataKey="monthLabel" hide />
        <YAxis hide />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0]?.payload as MonthData;
            return (
              <div className="bg-white border border-gray-200 rounded shadow p-2 text-xs">
                <p className="font-semibold">{d.monthLabel}{d.isPartial ? "*" : ""}</p>
                <p>Min: {fmtCurrencyFull(d.minimumRevenue)}</p>
                <p>Overage: {fmtCurrencyFull(d.overageRevenue)}</p>
              </div>
            );
          }}
        />
        <Bar dataKey="minimumRevenue" stackId="a" fill="#60a5fa" name="Minimum" />
        <Bar dataKey="overageRevenue" stackId="a" fill="#f97316" name="Overage" radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Customer status badge ─────────────────────────────────────────────────────

function StatusBadge({ d, hasMinimum }: { d: MonthData; hasMinimum: boolean }) {
  if (!hasMinimum) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"><Minus className="h-3 w-3" />No Minimum</span>;
  }
  if (d.isAboveMinimum) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle className="h-3 w-3" />Above Min</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3" />Below Min</span>;
}

// ── Expandable customer row ───────────────────────────────────────────────────

function CustomerRow({ customer }: { customer: CustomerResult }) {
  const [expanded, setExpanded] = useState(false);
  const currentData = customer.data[customer.data.length - 1];

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header row */}
      <button
        className="w-full flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors text-left"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        {/* Name + badge */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 text-sm truncate">{customer.name}</span>
            <StatusBadge d={currentData} hasMinimum={customer.hasMinimum} />
            {customer.billingStatus === "pending" && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-700 uppercase tracking-wide">Pending</span>
            )}
          </div>
          {customer.hasMinimum && (
            <p className="text-xs text-gray-500 mt-0.5">
              Min: {fmtMinutes(customer.minimumMinutes)} min/mo
              {currentData.isAboveMinimum
                ? <span className="text-green-600 ml-1">· {fmtMinutes(currentData.overageMinutes)} min overage this month</span>
                : <span className="text-red-500 ml-1">· {fmtMinutes(currentData.underutilization)} min underutilized this month</span>
              }
            </p>
          )}
        </div>

        {/* Sparkline */}
        <div className="w-40 hidden sm:block">
          <CustomerSparkline data={customer.data} hasMinimum={customer.hasMinimum} />
        </div>

        {/* Mini revenue bar */}
        <div className="w-32 hidden md:block">
          <CustomerMiniBar data={customer.data} />
        </div>

        {/* Current month revenue */}
        <div className="text-right min-w-[90px]">
          <p className="text-sm font-semibold text-gray-900">{fmtCurrencyFull(currentData.totalRevenue)}</p>
          {currentData.overageRevenue > 0 && (
            <p className="text-xs text-orange-600">+{fmtCurrencyFull(currentData.overageRevenue)} ovg</p>
          )}
          <p className="text-xs text-gray-400">{currentData.isPartial ? "this month" : currentData.monthLabel}</p>
        </div>

        {/* Expand toggle */}
        <div className="text-gray-400 ml-1">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {/* Expanded detail table */}
      {expanded && (
        <div className="border-t border-gray-100 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-500 uppercase tracking-wide text-[10px]">
                <th className="px-4 py-2 text-left font-medium">Month</th>
                <th className="px-3 py-2 text-right font-medium">Calls</th>
                <th className="px-3 py-2 text-right font-medium">Actual Min</th>
                {customer.hasMinimum && <th className="px-3 py-2 text-right font-medium">Min Threshold</th>}
                {customer.hasMinimum && <th className="px-3 py-2 text-right font-medium">Overage Min</th>}
                <th className="px-3 py-2 text-right font-medium">Min Revenue</th>
                <th className="px-3 py-2 text-right font-medium">Overage Rev</th>
                <th className="px-3 py-2 text-right font-medium">Total</th>
                {customer.hasMinimum && <th className="px-3 py-2 text-center font-medium">Status</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customer.data.map((d) => (
                <tr key={d.monthKey} className={d.isProjected ? "bg-amber-50/50" : d.isPartial ? "bg-blue-50/40" : "hover:bg-gray-50"}>
                  <td className="px-4 py-2 font-medium text-gray-800">
                    {d.monthLabel}
                    {d.isPartial    && <span className="ml-1 text-[10px] text-blue-500 font-normal">in progress</span>}
                    {d.isProjected  && <span className="ml-1 text-[10px] text-amber-600 font-normal">projected</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">{d.callCount.toLocaleString()}</td>
                  {/* #2: show actual + projected minutes separately for projected month */}
                  <td className="px-3 py-2 text-right text-gray-600">
                    {fmtMinutes(d.actualMinutes)}
                    {d.isProjected && d.projectedMinutes != null && (
                      <span className="block text-[10px] text-amber-600">→ {fmtMinutes(d.projectedMinutes)} proj.</span>
                    )}
                  </td>
                  {customer.hasMinimum && <td className="px-3 py-2 text-right text-gray-600">{fmtMinutes(d.minimumMinutes)}</td>}
                  {customer.hasMinimum && (
                    <td className={`px-3 py-2 text-right font-medium ${d.overageMinutes > 0 ? "text-orange-600" : "text-gray-400"}`}>
                      {d.overageMinutes > 0 ? fmtMinutes(d.overageMinutes) : "—"}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right text-gray-700">{fmtCurrencyFull(d.minimumRevenue)}</td>
                  <td className={`px-3 py-2 text-right font-medium ${d.overageRevenue > 0 ? "text-orange-600" : "text-gray-400"}`}>
                    {d.overageRevenue > 0 ? fmtCurrencyFull(d.overageRevenue) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900">
                    {fmtCurrencyFull(d.totalRevenue)}
                    {d.isProjected && d.actualTotalRevenue != null && (
                      <span className="block text-[10px] text-gray-400 font-normal">{fmtCurrencyFull(d.actualTotalRevenue)} actual</span>
                    )}
                  </td>
                  {customer.hasMinimum && (
                    <td className="px-3 py-2 text-center">
                      <StatusBadge d={d} hasMinimum={customer.hasMinimum} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── URL param helpers ─────────────────────────────────────────────────────────

function readParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    months:       parseInt(p.get("months") ?? "6", 10) as 3 | 6 | 12 | 24,
    monthView:    (["completed", "partial", "projected"].includes(p.get("monthView") ?? "") ? p.get("monthView") : "completed") as "completed" | "partial" | "projected",
    limit:        parseInt(p.get("limit") ?? "20", 10) as 10 | 20 | 50,
    statusFilter: (["all", "above", "below", "none"].includes(p.get("status") ?? "") ? p.get("status") : "all") as "all" | "above" | "below" | "none",
    search:       p.get("search") ?? "",
  };
}

export function BillableOverages() {
  const init = readParams();
  const [months,       setMonths]       = useState<3 | 6 | 12 | 24>(([3,6,12,24].includes(init.months) ? init.months : 6) as 3 | 6 | 12 | 24);
  const [monthView,    setMonthView]    = useState<"completed" | "partial" | "projected">(init.monthView);
  const [limit,        setLimit]        = useState<10 | 20 | 50>(([10,20,50].includes(init.limit) ? init.limit : 20) as 10 | 20 | 50);
  const [page,         setPage]         = useState(1);
  const [statusFilter, setStatusFilter] = useState<"all" | "above" | "below" | "none">(init.statusFilter);
  const [search,       setSearch]       = useState(init.search);
  const [data,         setData]         = useState<ApiData | null>(null);
  const [loading,      setLoading]      = useState(true);

  // Sync state → URL (no page reload, no extra DB queries)
  useEffect(() => {
    const p = new URLSearchParams();
    if (months !== 6)            p.set("months",    String(months));
    if (monthView !== "completed") p.set("monthView", monthView);
    if (limit !== 20)            p.set("limit",     String(limit));
    if (statusFilter !== "all")  p.set("status",    statusFilter);
    if (search)                  p.set("search",    search);
    const qs = p.toString();
    window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
  }, [months, monthView, limit, statusFilter, search]);

  // Fetch with AbortController — cancels stale in-flight requests on param change
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setPage(1);
    fetch(`/api/v1/overages-minimums?months=${months}&monthView=${monthView}`, { signal: controller.signal })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setData(res.data);
        else toast.error("Failed to load overages data");
      })
      .catch((err) => { if (err.name !== "AbortError") toast.error("Failed to load overages data"); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [months, monthView]);

  // Filter all customers client-side across ALL accounts (not just current page)
  const filteredCustomers = useMemo(() => {
    if (!data) return [];
    return data.customers.filter((c) => {
      const latestData = c.data[c.data.length - 1];
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "above" && latestData.isAboveMinimum) ||
        (statusFilter === "below" && c.hasMinimum && !latestData.isAboveMinimum) ||
        (statusFilter === "none" && !c.hasMinimum);
      const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [data, statusFilter, search]);

  // KPI tiles and charts reflect filtered accounts when a filter is active
  const isFiltered = statusFilter !== "all" || search !== "";
  const activeSummarySource = useCallback((monthKey: string | null) => {
    if (!data) return null;
    const mk = monthKey ?? data.months[data.months.length - 1];
    if (!isFiltered) {
      return data.summary.find((s) => s.monthKey === mk) ?? data.summary[data.summary.length - 1];
    }
    // Recompute summary from filtered customers for the target month
    const target = data.months.find((m) => m === mk) ?? data.months[data.months.length - 1];
    let totalCalls = 0, totalActualMinutes = 0, totalProjectedMinutes = 0;
    let totalMinimumRevenue = 0, totalOverageRevenue = 0, totalUsageRevenue = 0, totalRevenue = 0;
    let totalUnderutilizationMinutes = 0, accountsAboveMinimum = 0, accountsAtOrBelowMinimum = 0, accountsWithNoMinimum = 0;
    let isPartial = false, isProjected = false;
    for (const cr of filteredCustomers) {
      const d = cr.data.find((x) => x.monthKey === target);
      if (!d) continue;
      isPartial   = d.isPartial;
      isProjected = d.isProjected;
      totalCalls += d.callCount;
      totalActualMinutes += d.actualMinutes;
      totalProjectedMinutes += d.projectedMinutes ?? d.actualMinutes;
      totalMinimumRevenue += d.minimumRevenue;
      totalOverageRevenue += d.overageRevenue;
      totalUnderutilizationMinutes += d.underutilization;
      totalRevenue += d.totalRevenue;
      if (!cr.hasMinimum) { totalUsageRevenue += d.totalRevenue; accountsWithNoMinimum++; }
      else if (d.isAboveMinimum) accountsAboveMinimum++;
      else accountsAtOrBelowMinimum++;
    }
    return {
      monthKey: target, monthLabel: "", isPartial, isProjected,
      totalCalls, totalActualMinutes,
      totalProjectedMinutes: isProjected ? totalProjectedMinutes : null,
      totalMinimumRevenue, totalOverageRevenue, totalUsageRevenue, totalRevenue,
      totalUnderutilizationMinutes, accountsAboveMinimum, accountsAtOrBelowMinimum, accountsWithNoMinimum,
    };
  }, [data, filteredCustomers, isFiltered]);

  // KPI tiles use filtered summary for the current/latest month
  const currentSummary = useMemo(
    () => activeSummarySource(data?.currentMonthKey ?? null),
    [activeSummarySource, data]
  );

  // Chart summaries — per-month filtered or unfiltered
  const chartSummary = useMemo(() => {
    if (!data) return [];
    if (!isFiltered) return data.summary;
    return data.months.map((mk) => activeSummarySource(mk) ?? data.summary.find((s) => s.monthKey === mk)!);
  }, [data, isFiltered, activeSummarySource]);

  // Paginate filtered results client-side
  const totalFiltered = filteredCustomers.length;
  const totalPages    = Math.ceil(totalFiltered / limit) || 1;
  const safePage      = Math.min(page, totalPages);
  const pagedCustomers = useMemo(
    () => filteredCustomers.slice((safePage - 1) * limit, safePage * limit),
    [filteredCustomers, safePage, limit]
  );

  // Chart data — uses filtered summary when filter is active
  const revenueChartData = useMemo(() =>
    chartSummary.map((s) => ({
      name: s.isProjected ? `${s.monthLabel} (est.)` : s.monthLabel,
      "Minimum Revenue": s.totalMinimumRevenue,
      "Overage Revenue": s.totalOverageRevenue,
      "Usage Revenue":   s.totalUsageRevenue,
      isPartial:   s.isPartial,
      isProjected: s.isProjected,
    }))
  , [chartSummary]);

  const volumeChartData = useMemo(() =>
    chartSummary.map((s) => ({
      name: s.isProjected ? `${s.monthLabel} (est.)` : s.monthLabel,
      Calls: s.totalCalls,
      Minutes: Math.round(s.isProjected && s.totalProjectedMinutes != null ? s.totalProjectedMinutes : s.totalActualMinutes),
      isPartial:   s.isPartial,
      isProjected: s.isProjected,
    }))
  , [chartSummary]);

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading overages data…</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto p-6">
        <p className="text-gray-500">No data available.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">

      {/* Page header — row 1: title left, filters right */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billable Overages & Minimums</h1>
          <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
            <Info className="h-3.5 w-3.5" />
            Estimated using current rates & minimums — not invoiced amounts
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {/* Row 1: filters */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <input
              type="text"
              placeholder="Search accounts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 w-44"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Accounts</option>
              <option value="above">Above Minimum</option>
              <option value="below">Below Minimum</option>
              <option value="none">No Minimum</option>
            </select>
            <select
              value={limit}
              onChange={(e) => { setLimit(parseInt(e.target.value) as 10 | 20 | 50); setPage(1); }}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
            <select
              value={months}
              onChange={(e) => { setMonths(parseInt(e.target.value) as 3 | 6 | 12 | 24); setPage(1); }}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={3}>Last 3 Months</option>
              <option value={6}>Last 6 Months</option>
              <option value={12}>Last 12 Months</option>
              <option value={24}>Last 24 Months</option>
            </select>
            <select
              value={monthView}
              onChange={(e) => { setMonthView(e.target.value as typeof monthView); setPage(1); }}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="completed">Through Last Month</option>
              <option value="partial">Partial</option>
              <option value="projected">Actual + Projected</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      {currentSummary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Overage Revenue */}
          <div className="bg-white p-5 rounded-lg shadow border">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Overage Revenue</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{fmtCurrency(currentSummary.totalOverageRevenue)}</p>
            <p className="text-xs text-gray-400 mt-1">
              {monthView === "completed" ? "last completed month" : monthView === "projected" ? "projected full month" : "this month (in progress)"}
            </p>
          </div>

          {/* Minimum Revenue */}
          <div className="bg-white p-5 rounded-lg shadow border">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Minimum Revenue</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{fmtCurrency(currentSummary.totalMinimumRevenue)}</p>
            <p className="text-xs text-gray-400 mt-1">base from {currentSummary.accountsAboveMinimum + currentSummary.accountsAtOrBelowMinimum} accounts w/ minimums</p>
          </div>

          {/* Accounts Above Minimum */}
          <div className="bg-white p-5 rounded-lg shadow border">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Accounts Above Min</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{currentSummary.accountsAboveMinimum}</p>
            <p className="text-xs text-gray-400 mt-1">
              of {currentSummary.accountsAboveMinimum + currentSummary.accountsAtOrBelowMinimum} with minimums
            </p>
          </div>

          {/* Underutilization */}
          <div className="bg-white p-5 rounded-lg shadow border">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Underutilization</p>
            <p className="text-2xl font-bold text-red-500 mt-1">{fmtMinutes(currentSummary.totalUnderutilizationMinutes)} min</p>
            <p className="text-xs text-gray-400 mt-1">paid-for minutes not used {monthView === "completed" ? "last month" : "this month"}</p>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Stacked bar: min revenue vs overage revenue */}
        <div className="bg-white p-5 rounded-lg shadow border">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Revenue Breakdown Over Time</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={revenueChartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={52} />
              <Tooltip content={<RevenueTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Minimum Revenue" stackId="a" fill="#60a5fa" />
              <Bar dataKey="Overage Revenue" stackId="a" fill="#f97316" />
              <Bar dataKey="Usage Revenue" stackId="a" fill="#a78bfa" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Dual-axis line: calls vs minutes */}
        <div className="bg-white p-5 rounded-lg shadow border">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Call Volume vs Minutes Over Time</h2>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={volumeChartData} margin={{ top: 4, right: 32, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="calls" tick={{ fontSize: 11 }} width={44} />
              <YAxis yAxisId="minutes" orientation="right" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={44} />
              <Tooltip content={<VolumeTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="calls" type="monotone" dataKey="Calls" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line yAxisId="minutes" type="monotone" dataKey="Minutes" stroke="#10b981" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-customer list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-800">
            Accounts
            <span className="ml-2 text-gray-400 font-normal text-xs">({totalFiltered} shown)</span>
          </h2>
          <p className="text-xs text-gray-400">
            Sorted by {monthView === "completed" ? "last completed month" : "latest period"} overage revenue
          </p>
        </div>

        {pagedCustomers.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400 text-sm">
            No accounts match the current filter.
          </div>
        ) : (
          <div className="space-y-2">
            {pagedCustomers.map((c) => (
              <CustomerRow key={c.id} customer={c} />
            ))}
          </div>
        )}

        {/* Pagination controls — fully client-side, works across all filtered results */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-gray-500">
              Showing {((safePage - 1) * limit) + 1}–{Math.min(safePage * limit, totalFiltered)} of {totalFiltered} accounts
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={safePage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">«</button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">‹ Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => Math.abs(p - safePage) <= 2)
                .map((p) => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`px-2.5 py-1 text-xs rounded border ${p === safePage ? "bg-blue-600 text-white border-blue-600 font-semibold" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >{p}</button>
                ))}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next ›</button>
              <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">»</button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
